// Crew: hiring pool, wages, fatigue, morale, skill/xp, traits, injuries, quitting (DESIGN §4).
import { chance, range, int, pick, normal, nextFloat } from '../core/rng.js';
import { dayToDate } from '../core/time.js';
import { ROLES, roleExists, marketWage, eqDef, rigStats, yearOf } from './defs.js';
import { clamp, nextId, notify, log, spend } from './util.js';
import { handleDeath } from './safety.js';

const FIRST = ['Billy', 'Edwin', 'Jonathan', 'Ezra', 'Silas', 'Amos', 'Henry', 'Jacob', 'Patrick', 'Michael', 'Owen', 'Thomas', 'Samuel', 'Uncle Billy', 'Elias', 'Caleb', 'Josiah', 'Seamus', 'Hiram', 'Moses', 'Abner', 'Levi', 'Isaac', 'Walter', 'George', 'Frank', 'Conrad', 'Dutch', 'Lars', 'Nils', 'Mary', 'Abigail', 'Ida', 'Sarah'];
const LAST = ['Smith', 'Watson', 'Hibbard', 'Tarr', 'McClintock', 'Rouse', 'Brewer', 'Ferris', 'Kier', 'Densmore', 'Funk', 'Phillips', 'Bissell', 'Evans', 'Gale', 'Hyde', 'Dunn', 'Murphy', 'O\'Brien', 'Schmidt', 'Keller', 'Larsen', 'Booth', 'Carver', 'Whitaker', 'Pratt', 'Haines', 'Wilson', 'Grady', 'Steele'];
// polish (C4): era name pools. Same number of RNG draws as before (one per name part), so seeded outcomes are
// unchanged; the 1859 pool is the original. A clash with a current hand or applicant steps to the next surname.
const FIRST_1901 = ['Walter', 'Clarence', 'Earl', 'Roy', 'Lee', 'Floyd', 'Homer', 'Otis', 'Chester', 'Harvey', 'Lloyd', 'Elmer', 'Ira', 'Luther', 'Virgil', 'Claude', 'Grady', 'Wiley', 'Jesse', 'Arch', 'Curt', 'Pat', 'Mike', 'Joe', 'Frank', 'Charlie', 'Will', 'Ed', 'Tom', 'Jim', 'Dutch', 'Slim', 'Red', 'Shorty'];
const FIRST_1946 = ['Bill', 'Bob', 'Jack', 'Don', 'Ray', 'Gene', 'Harold', 'Kenneth', 'Wayne', 'Dale', 'Jimmy', 'Buddy', 'Carl', 'Leon', 'Glen', 'Darrell', 'Troy', 'Bobby', 'Jerry', 'Doyle', 'Vernon', 'Marvin', 'Earl', 'Clyde', 'Wendell', 'Joe', 'Frank', 'Tex', 'Red', 'Slim', 'Rusty', 'Manuel', 'Ruben', 'Alton'];
const FIRST_1973 = ['Mike', 'Steve', 'Gary', 'Larry', 'Randy', 'Rick', 'Terry', 'Danny', 'Ronnie', 'Dennis', 'Mark', 'David', 'Kenny', 'Jeff', 'Greg', 'Roger', 'Wayne', 'Tony', 'Rodney', 'Dwayne', 'Ray', 'Jim', 'Bubba', 'Hector', 'Luis', 'Andy', 'Doug', 'Craig', 'Ian', 'Gordon', 'Alasdair', 'Linda', 'Debbie', 'Carlos'];
const LAST_1901 = ['Johnson', 'Williams', 'Brown', 'Davis', 'Miller', 'Wilson', 'Moore', 'Taylor', 'Anderson', 'Jackson', 'White', 'Harris', 'Martin', 'Thompson', 'Walker', 'Hall', 'Allen', 'Young', 'King', 'Wright', 'Scott', 'Green', 'Baker', 'Nelson', 'Carter', 'Mitchell', 'Turner', 'Campbell', 'Parker', 'Collins', 'Stewart', 'Morris', 'Murphy', 'Rogers', 'Reed', 'Kelly', 'Garza', 'Hernández', 'Guidry', 'Broussard', 'Thibodeaux', 'Landry', 'Fontenot', 'Sharp', 'Byrd', 'Sterling', 'Pruitt', 'Tatum', 'McBride', 'Duncan'];
const NAME_POOLS = [[1973, FIRST_1973, LAST_1901], [1946, FIRST_1946, LAST_1901], [1901, FIRST_1901, LAST_1901]];
const takenNames = (state) => new Set([...Object.values(state.crew ?? {}), ...(state.hirePool ?? []), ...(state._namePool ?? [])].map((c) => c?.name));
// perf: refreshHirePool builds ~60 applicants; the taken-name set is built once and grown, not rebuilt per name
let NAME_BATCH = null;
function crewName(state) {
  const y = yearOf(state);
  const [, F, L] = NAME_POOLS.find(([from]) => y >= from) ?? [0, FIRST, LAST];
  const first = pick(state, F);
  const li = Math.floor(nextFloat(state) * L.length);
  const taken = NAME_BATCH ?? takenNames(state);
  for (let k = 0; k < L.length; k++) { const n = `${first} ${L[(li + k) % L.length]}`; if (!taken.has(n)) return n; }
  return `${first} ${L[li]}`;
}
export const TRAITS = ['old_hand', 'reckless', 'careful', 'natural_leader', 'lucky', 'drinker'];
export const TRAIT_NAMES = { old_injury: 'Old Injury', old_hand: 'Old Hand', reckless: 'Reckless', careful: 'Careful', natural_leader: 'Natural Leader', lucky: 'Lucky', drinker: 'Drinker', genius: 'Genius', crack_shot: 'Crack Shot' };

const POOL_ROLES_BASE = ['driller', 'tool_dresser', 'laborer', 'teamster', 'geologist', 'guard', 'inventor',
  // M2 (each appears from its ROLES fromYear, so the Pioneer pool and its RNG draws are unchanged)
  'derrickman', 'roughneck', 'motorman', 'toolpusher', 'safety_officer', 'mud_man', 'roustabout', 'rig_builder', 'gauger', 'pumper', 'geophysicist',
  // M3 ops (fromYear >= 1946: M1/M2 pools unchanged; contracted divers/pilots are hired per job, not here)
  'mud_engineer', 'petroleum_engineer', 'crane_op', 'oim', 'medic', 'cook', 'ballast_control_op', 'subsea_engineer', 'mud_logger', 'landman', 'counsel'];

export function rolesHireable(state) {
  const y = yearOf(state);
  return POOL_ROLES_BASE.filter((r) => {
    if (!roleExists(r)) return r === 'guard'; // guard fallback until the data lands
    return (ROLES[r].fromYear ?? 1859) <= y;
  });
}

export function makeCrew(state, role, skill = null, { traits = null } = {}) {
  const rep = state.company?.reputation ?? 30;
  const sk = skill ?? Math.round(clamp(normal(state, 25 + rep * 0.5, 14), 5, 95));
  let tr = traits;
  if (!tr) {
    tr = [];
    const pool = [...TRAITS];
    if (role === 'inventor') pool.push('genius', 'genius');
    if (role === 'guard') pool.push('crack_shot', 'crack_shot');
    for (let k = 0; k < 2; k++) if (chance(state, 0.3)) { const t = pick(state, pool); if (!tr.includes(t) && !(t === 'reckless' && tr.includes('careful')) && !(t === 'careful' && tr.includes('reckless'))) tr.push(t); }
  }
  return {
    id: nextId(state, 'c'), name: crewName(state), role, skill: sk, xp: 0,
    morale: Math.round(range(state, 50, 70)), fatigue: 0, age: int(state, 18, 52), traits: tr,
    wage: Math.round(marketWage(role, yearOf(state), sk) * 100) / 100,
    rigId: null, labUid: null, siteId: null, hiredDay: null, injuredUntil: null, status: 'active',
  };
}

export function refreshHirePool(state) {
  const pool = [];
  state._namePool = pool; // polish: lets crewName() avoid duplicates inside the batch being built
  NAME_BATCH = takenNames(state);
  try {
    for (const role of rolesHireable(state)) {
      const n = role === 'geologist' || role === 'inventor' ? int(state, 1, 3) : int(state, 2, 4);
      for (let i = 0; i < n; i++) { const c = makeCrew(state, role); pool.push(c); NAME_BATCH.add(c.name); }
    }
  } finally { NAME_BATCH = null; }
  delete state._namePool;
  state.hirePool = pool;
}

export const isActive = (c) => c.status === 'active' && !c.injuredUntil;

export function activeRigCrew(state, rig) {
  return rig.crewIds.map((id) => state.crew[id]).filter((c) => c && isActive(c));
}

export function crewHas(crew, role) { return crew.some((c) => c.role === role); }
export function traitCount(crew, trait) { return crew.filter((c) => c.traits?.includes(trait)).length; }

export function crewWage(state, c) {
  const base = marketWage(c.role, yearOf(state), c.skill);
  const mult = state.company.wageMult?.[c.role] ?? 1;
  const rig = c.rigId ? state.rigs[c.rigId] : null;
  const tourMult = rig ? ({ 1: 1, 2: 1.5, 3: 2 }[rig.tours] ?? 1) : 1;
  return base * mult * tourMult;
}

// Injure (or kill) a crew member. opts: { cause, fatalChance, days: [lo,hi], wellId, accident, silent, preventable }
export function injureCrew(state, c, why = 'was hurt on the job', opts = {}) {
  if (!c || c.status !== 'active') return null;
  const { cause = why, fatalChance = 0.05, days = [10, 60], wellId = null, accident = null, silent = false, preventable = false } = opts;
  state.company.stats.injuries++;
  state.company.accidentFreeSince = state.day;
  const rig = c.rigId ? state.rigs[c.rigId] : null;
  if (rig) {
    for (const o of activeRigCrew(state, rig)) o.morale = Math.max(0, o.morale - 15);
    if (rig.safety) rig.safety.daysWithoutIncident = 0;
  }
  if (state.safety) state.safety.daysWithoutIncident = 0;
  const year = dayToDate(state.day).year;
  if (chance(state, fatalChance)) {
    c.status = 'dead';
    c.leftDay = state.day;
    removeFromAssignments(state, c);
    state.company.stats.deaths++;
    state.company.reputation = Math.max(0, state.company.reputation - (preventable ? 20 : 10));
    (state.memorial ??= []).push({ name: c.name, role: c.role, born: year - c.age, died: year, day: state.day, cause, wellId });
    for (const o of Object.values(state.crew)) if (o.status === 'active') o.morale = Math.max(0, o.morale - 30);
    handleDeath(state, c, cause);
    if (!silent) notify(state, 'injury', { crewId: c.id, wellId, fatal: true, accident, pause: true, text: `${c.name} (${roleName(c.role)}) died — ${why}.`, level: 'warn' });
    return 'dead';
  }
  const d = int(state, days[0], days[1]);
  c.injuredUntil = state.day + d;
  if (d > 45 && chance(state, 0.4) && !c.traits.includes('old_injury')) { c.traits.push('old_injury'); c.skill = Math.max(1, c.skill - 10); }
  if (!silent) notify(state, 'injury', { crewId: c.id, wellId, accident, pause: true, text: `${c.name} (${roleName(c.role)}) ${why}; out for ${d} days.`, level: 'warn' });
  return 'injured';
}


export const roleName = (r) => ROLES[r]?.name ?? r.replace(/_/g, ' ');

export function removeFromAssignments(state, c) {
  if (c.rigId && state.rigs[c.rigId]) state.rigs[c.rigId].crewIds = state.rigs[c.rigId].crewIds.filter((x) => x !== c.id);
  c.rigId = null;
  if (c.siteId) unassignGuard(state, c);
  c.labUid = null;
}

export function unassignGuard(state, c) {
  const site = findSite(state, c.siteId);
  if (site?.security) site.security.guards = site.security.guards.filter((x) => x !== c.id);
  c.siteId = null;
}

// Security sites are rigs, wells or fields (by id prefix / lookup).
export function findSite(state, siteId) {
  if (!siteId) return null;
  return state.rigs[siteId] ?? state.wells[siteId] ?? state.fields[siteId] ?? null;
}

const WORKING = new Set(['DRILLING', 'TRIPPING', 'RUNNING_CASING', 'FISHING', 'CLEANOUT', 'GUSHER', 'COMPLETING', 'GAS_BLOW', 'RIGGING_UP']);

function rigActivity(state, rig) {
  const w = rig.wellId ? state.wells[rig.wellId] : null;
  if (!w) return { working: false, risk: 0.1 };
  if (w.status === 'DRILLING' && w.problem) return { working: false, risk: 0.3 };
  if (w.status === 'GUSHER') return { working: true, risk: 4 };
  if (WORKING.has(w.status)) return { working: true, risk: 1 };
  if (w.lift?.usesRig) return { working: true, risk: 0.5 };
  return { working: false, risk: 0.2 };
}

export function tickCrew(state) {
  const { year, month, dom } = dayToDate(state.day);
  let wages = 0;
  const all = Object.values(state.crew).filter((c) => c.status === 'active');
  // perf: per-rig "has a natural leader on the crew" — cleared whenever anyone's active status changes in this loop
  const leader = new Map();
  for (const c of all) {
    if (c.injuredUntil && c.injuredUntil <= state.day) { c.injuredUntil = null; leader.clear(); log(state, `${c.name} is back on his feet.`); }
    const w = crewWage(state, c);
    c.wage = Math.round(w * 100) / 100;
    wages += c.standby ? w * 0.5 : c.injuredUntil ? w * 0.5 : w;   // standby retainer = half pay (sim/austerity.js)
    if (c.injuredUntil) continue;
    if (c.standby) {
      // waiting for work: rested, but morale sags toward 30 and long waits lose men
      c.fatigue = clamp(c.fatigue - 10, 0, 100);
      c.morale = clamp(c.morale + (30 - c.morale) * 0.01, 0, 100);
      if (c.morale < 20 && chance(state, 0.01)) {
        c.status = 'quit'; c.standby = null; leader.clear();
        notify(state, 'injury', { crewId: c.id, quit: true, text: `${c.name} gave up waiting on standby and left for another outfit.`, level: 'warn' });
      }
      continue;
    }

    const rig = c.rigId ? state.rigs[c.rigId] : null;
    const act = rig ? rigActivity(state, rig) : { working: !!(c.siteId || c.labUid), risk: c.siteId ? 0.5 : 0.05 };
    if (act.working) c.fatigue = clamp(c.fatigue + (rig ? ({ 1: 1.5, 2: 3, 3: 1.5 }[rig.tours] ?? 1.5) : 1), 0, 100);
    else c.fatigue = clamp(c.fatigue - 10, 0, 100);

    // morale drift
    const pctAbove = ((state.company.wageMult?.[c.role] ?? 1) - 1) * 100;
    let lead = rig ? leader.get(rig) : false;
    if (lead === undefined) leader.set(rig, (lead = traitCount(activeRigCrew(state, rig), 'natural_leader') > 0));
    let target = 50 + 0.5 * pctAbove + (lead ? 5 : 0) - (c.fatigue > 70 ? 10 : 0);
    if (c.traits.includes('drinker') && chance(state, 1 / 7)) c.morale += range(state, -8, 6);
    c.morale = clamp(c.morale + (target - c.morale) * 0.03, 0, 100);

    // skill
    while (c.xp >= Math.max(10, c.skill * 3) && c.skill < 100) { c.xp -= Math.max(10, c.skill * 3); c.skill++; }

    // (safety incidents are rolled in safety.js)

    // quitting
    if (c.morale < 20 && chance(state, 0.02)) {
      c.status = 'quit'; leader.clear();
      removeFromAssignments(state, c);
      notify(state, 'injury', { crewId: c.id, quit: true, text: `${c.name} quit in disgust (morale ${Math.round(c.morale)}).`, level: 'warn' });
    }
  }
  if (wages > 0) spend(state, wages, 'wages', false);

  // birthdays & retirement on 1 Jan
  if (month === 1 && dom === 1) {
    for (const c of all) {
      if (c.status !== 'active') continue;
      c.age++;
      if (c.age >= 60 && chance(state, (c.age - 59) / 10)) {
        c.status = 'retired';
        removeFromAssignments(state, c);
        log(state, `${c.name} retired at ${c.age}${c.skill >= 90 ? ' — a legend of the Oil Region' : ''}.`);
      }
    }
  }
  // purge departed crew older than a year to keep saves small
  for (const c of Object.values(state.crew)) if (c.status !== 'active' && c.leftDay && state.day - c.leftDay > 365) delete state.crew[c.id];
  for (const c of Object.values(state.crew)) if (c.status !== 'active' && !c.leftDay) c.leftDay = state.day;

  if ((state.day - (state._poolDay ?? state.day)) >= 7 || state._poolDay == null) {
    if (state._poolDay != null) refreshHirePool(state);
    state._poolDay = state.day;
  }
}

// ---- action implementations ----
export function hireImpl(state, candidateId) {
  const i = state.hirePool.findIndex((c) => c.id === candidateId);
  if (i < 0) return { ok: false, msg: 'That candidate is gone.' };
  const c = state.hirePool[i];
  const signing = c.wage; // one day's pay up front
  if (!spend(state, signing, 'wages')) return { ok: false, msg: 'Not enough cash to hire.' };
  state.hirePool.splice(i, 1);
  c.hiredDay = state.day;
  state.crew[c.id] = c;
  let where = '';
  // auto-assign to a rig that is missing this role
  for (const rig of Object.values(state.rigs)) {
    const req = rigStats(eqDef(rig.defId)).requiredRoles ?? {};
    const have = rig.crewIds.filter((id) => state.crew[id]?.role === c.role && state.crew[id].status === 'active').length;
    if ((req[c.role] ?? 0) > have) { rig.crewIds.push(c.id); c.rigId = rig.uid; where = ` and sent to ${rig.name}`; break; }
  }
  return { ok: true, msg: `Hired ${c.name}, ${roleName(c.role)} (skill ${c.skill}, $${c.wage.toFixed(2)}/day)${where}.` };
}

// Hire a fireman straight onto a steam rig (boiler.js): not drawn from the weekly applicant pool, so the pool and its
// RNG draws are unchanged for everyone who never hires one.
export function hireFiremanImpl(state, rigUid) {
  const rig = state.rigs?.[rigUid];
  if (!rig) return { ok: false, msg: 'No such rig.' };
  if (!rig.engineUid || !/steam/.test(state.items[rig.engineUid]?.defId ?? '')) return { ok: false, msg: `${rig.name} has no steam boiler to tend.` };
  if (rig.crewIds.some((id) => state.crew[id]?.role === 'fireman' && state.crew[id].status === 'active')) return { ok: false, msg: `${rig.name} already has a fireman.` };
  const c = makeCrew(state, 'fireman');
  if (!spend(state, c.wage, 'wages')) return { ok: false, msg: 'Not enough cash to hire.' };
  c.hiredDay = state.day; c.rigId = rig.uid;
  state.crew[c.id] = c; rig.crewIds.push(c.id);
  return { ok: true, msg: `Hired ${c.name} as fireman on ${rig.name} ($${c.wage.toFixed(2)}/day): the boiler is tended full time.` };
}

export function fireImpl(state, crewId) {
  const c = state.crew[crewId];
  if (!c || c.status !== 'active') return { ok: false, msg: 'No such crew member.' };
  removeFromAssignments(state, c);
  c.status = 'fired';
  c.leftDay = state.day;
  if (yearOf(state) >= 1970) spend(state, c.wage * 10, 'wages', false);
  for (const o of Object.values(state.crew)) if (o.status === 'active') o.morale = Math.max(0, o.morale - 5);
  return { ok: true, msg: `${c.name} let go.` };
}

export function assignCrewImpl(state, crewId, rigUid) {
  const c = state.crew[crewId];
  if (!c || c.status !== 'active') return { ok: false, msg: 'No such crew member.' };
  if (rigUid && !state.rigs[rigUid]) return { ok: false, msg: 'No such rig.' };
  if (rigUid && c.standby) return { ok: false, msg: `${c.name} is on standby — recall him first.` };
  if (rigUid && state.rigs[rigUid].mothballed) return { ok: false, msg: `${state.rigs[rigUid].name} is in storage.` };
  removeFromAssignments(state, c);
  if (rigUid) { state.rigs[rigUid].crewIds.push(c.id); c.rigId = rigUid; return { ok: true, msg: `${c.name} assigned to ${state.rigs[rigUid].name}.` }; }
  return { ok: true, msg: `${c.name} is in the bunkhouse.` };
}
